import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { CreateInviteDialog, inviteLink } from "@/components/CreateInviteDialog";
import { Ticket, Loader2, Copy, Check, Link2, Mail, Ban, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  active: { label: "Active", color: "#10B981" },
  disabled: { label: "Disabled", color: "#71717A" },
  used: { label: "Used", color: "#002EA6" },
};

export default function AdminInvites() {
  const [invites, setInvites] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => api.get("/candidate-invites").then(({ data }) => setInvites(data));
  useEffect(() => { load(); }, []);

  const copy = (inv) => {
    navigator.clipboard.writeText(inviteLink(inv.token));
    setCopiedId(inv.id);
    toast.success("Invite link copied");
    setTimeout(() => setCopiedId(null), 2000);
  };
  const toggle = async (inv) => {
    try { await api.post(`/candidate-invites/${inv.id}/toggle`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (inv) => {
    if (!window.confirm("Delete this invite link? Anyone still holding it will no longer be able to join.")) return;
    try { await api.delete(`/candidate-invites/${inv.id}`); toast.success("Invite deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Candidate onboarding" title="Invites">
        <CreateInviteDialog onCreated={load} />
      </PageHeader>

      <div className="p-8">
        {!invites && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {invites && invites.length === 0 && <Empty icon={Ticket} title="No invites yet" hint="Generate a reusable link or a one-time email invite to onboard candidates to the portal." />}
        {invites && invites.length > 0 && (
          <div className="border border-black/10">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-black/10 overline text-[0.55rem] bg-white">
              <span className="col-span-3">Type</span><span className="col-span-3">For</span><span className="col-span-2">Status</span><span className="col-span-4 text-right">Actions</span>
            </div>
            {invites.map((inv) => {
              const st = STATUS[inv.status] || STATUS.active;
              return (
                <div key={inv.id} className="grid grid-cols-12 px-5 py-4 border-b border-black/5 bg-white items-center" data-testid={`invite-row-${inv.id}`}>
                  <span className="col-span-3 text-sm flex items-center gap-2 capitalize">
                    {inv.type === "single" ? <Mail className="h-4 w-4 text-muted-foreground" /> : <Link2 className="h-4 w-4 text-muted-foreground" />}
                    {inv.type === "single" ? "One-time" : "Reusable"}
                  </span>
                  <span className="col-span-3 text-sm truncate">
                    {inv.type === "single" ? inv.email : <span className="text-muted-foreground">{inv.signup_count || 0} signup{(inv.signup_count || 0) === 1 ? "" : "s"}</span>}
                  </span>
                  <span className="col-span-2 flex items-center gap-1.5 text-xs"><span className="h-1.5 w-1.5 rounded-full" style={{ background: st.color }} />{st.label}</span>
                  <span className="col-span-4 flex items-center justify-end gap-2">
                    <button onClick={() => copy(inv)} data-testid={`invite-copy-${inv.id}`} title="Copy link" className="p-1.5 border border-black/10 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors">
                      {copiedId === inv.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                    {inv.status !== "used" && (
                      <button onClick={() => toggle(inv)} data-testid={`invite-toggle-${inv.id}`} title={inv.status === "active" ? "Disable" : "Enable"} className="p-1.5 border border-black/10 text-muted-foreground hover:text-[#09090B] transition-colors">
                        {inv.status === "active" ? <Ban className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <button onClick={() => remove(inv)} data-testid={`invite-delete-${inv.id}`} title="Delete" className="p-1.5 border border-black/10 text-muted-foreground hover:text-[#EF4444] hover:border-[#EF4444]/40 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
